import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './IbisTrailWalker.css';

/**
 * « Ibis marcheur » (design 3a, Jogolinga - Pas animés).
 *
 * Footprints replace the dashed connectors of the lesson trail. On mount the
 * ibis walks the last few connectors leading to the current lesson, leaving
 * footprints that stay, dips into each lesson it reaches (with an ochre ring),
 * then stops on the current lesson. The rest of the path fades in, ghosted.
 *
 * Geometry is measured from the DOM: each `.sentier-step` carries
 * `data-trail-idx` and `data-has-conn`, and its `.sentier-node-wrap` gives the
 * node centre. Everything is drawn in an overlay under the steps.
 */

type Pt = [number, number];

interface Seg {
  from: number;          // step index the connector starts from
  to: number;            // step index it leads to
  pts: Pt[];             // chamfered polyline, trail-relative px
  len: number;
}

interface Geometry {
  width: number;
  height: number;
  segs: Seg[];
  nodes: Map<number, { cx: number; cy: number; top: number }>;
}

interface Props {
  /** Index (in the displayed trail) of the lesson the ibis stops on. */
  currentIndex: number;
  /** Changes whenever the trail's steps change, to re-measure. */
  layoutKey: string;
  /** Called once the overlay has measured the trail and is drawing. */
  onReady?: (ready: boolean) => void;
}

const SPEED = 85;          // px / s
const STEP_GAP = 17;       // px between footprints
const WALK_SEGMENTS = 3;   // connectors walked before the current lesson
const SPRITE = 46;         // px
const LEAD_STOP = 0.55;    // lead walk stops at this fraction of the connector
const ANCHOR: Pt = [0.5, 0.965]; // feet position inside the sprite box

// ── Geometry helpers (from the design file) ────────────────────────────────
function chamfer(pts: Pt[], r = 18): Pt[] {
  const out: Pt[] = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const a = pts[i - 1], c = pts[i], b = pts[i + 1];
    const l1 = Math.hypot(c[0] - a[0], c[1] - a[1]);
    const l2 = Math.hypot(b[0] - c[0], b[1] - c[1]);
    if (l1 < 0.5 || l2 < 0.5) { out.push(c); continue; }
    const rr = Math.min(r, l1 / 2, l2 / 2);
    const d1: Pt = [(c[0] - a[0]) / l1, (c[1] - a[1]) / l1];
    const d2: Pt = [(b[0] - c[0]) / l2, (b[1] - c[1]) / l2];
    out.push(
      [c[0] - d1[0] * rr, c[1] - d1[1] * rr],
      [c[0] - d1[0] * rr * .35 + d2[0] * rr * .35, c[1] - d1[1] * rr * .35 + d2[1] * rr * .35],
      [c[0] + d2[0] * rr, c[1] + d2[1] * rr],
    );
  }
  out.push(pts[pts.length - 1]);
  return out;
}

const polyLen = (pts: Pt[]) =>
  pts.slice(1).reduce((s, p, i) => s + Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]), 0);

function pointAt(pts: Pt[], d: number): { x: number; y: number; ux: number } {
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (d <= L || i === pts.length - 2) {
      const k = L > 0 ? Math.min(1, Math.max(0, d / L)) : 0;
      return { x: a[0] + (b[0] - a[0]) * k, y: a[1] + (b[1] - a[1]) * k, ux: L > 0 ? (b[0] - a[0]) / L : 0 };
    }
    d -= L;
  }
  const p = pts[pts.length - 1];
  return { x: p[0], y: p[1], ux: 0 };
}

interface Step { x: number; y: number; ang: number; nx: number; ny: number; side: number; d: number }

function sample(pts: Pt[], gap: number): Step[] {
  const s: Step[] = [];
  let acc = gap / 2, base = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L < 0.01) continue;
    const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L;
    while (acc <= L) {
      s.push({
        x: a[0] + ux * acc, y: a[1] + uy * acc,
        ang: Math.atan2(uy, ux) * 180 / Math.PI + 90,
        nx: -uy, ny: ux, side: s.length % 2 ? 1 : -1, d: base + acc,
      });
      acc += gap;
    }
    acc -= L; base += L;
  }
  return s;
}

function measure(container: HTMLDivElement): Geometry | null {
  const box = container.getBoundingClientRect();
  if (box.width === 0) return null;
  const stepEls = Array.from(container.querySelectorAll<HTMLElement>('[data-trail-idx]'));
  const info = new Map<number, { el: HTMLElement; cx: number; cy: number; top: number; bottom: number; conn: boolean }>();
  stepEls.forEach(el => {
    const idx = Number(el.dataset.trailIdx);
    const wrap = el.querySelector<HTMLElement>('.sentier-node-wrap');
    if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    const sr = el.getBoundingClientRect();
    info.set(idx, {
      el,
      cx: r.left + r.width / 2 - box.left,
      cy: r.top + r.height / 2 - box.top,
      top: r.top - box.top,
      bottom: sr.bottom - box.top,
      conn: el.dataset.hasConn === '1',
    });
  });
  const segs: Seg[] = [];
  info.forEach((a, idx) => {
    const b = info.get(idx + 1);
    if (!a.conn || !b) return;
    // Same shape as the old dashed connector: down from under the label,
    // across, then a short drop onto the next node's ring.
    const connTop = a.bottom - 56;
    const midY = connTop + 30;
    const endY = Math.max(midY + 8, b.top - 8);
    const pts = chamfer([[a.cx, connTop], [a.cx, midY], [b.cx, midY], [b.cx, endY]]);
    segs.push({ from: idx, to: idx + 1, pts, len: polyLen(pts) });
  });
  segs.sort((x, y) => x.from - y.from);
  const nodes = new Map<number, { cx: number; cy: number; top: number }>();
  info.forEach((v, k) => nodes.set(k, { cx: v.cx, cy: v.cy, top: v.top }));
  return { width: box.width, height: container.scrollHeight, segs, nodes };
}

// ── Timeline ───────────────────────────────────────────────────────────────
type Phase =
  | { kind: 'appear'; t0: number; dur: number; at: Pt; face: number }
  | { kind: 'walk'; t0: number; dur: number; seg: Seg; face: number; upTo?: number }
  | { kind: 'dive'; t0: number; dur: number; at: Pt; face: number }
  | { kind: 'hidden'; t0: number; dur: number; at: Pt; face: number }
  | { kind: 'emerge'; t0: number; dur: number; at: Pt; face: number };

interface Timeline {
  phases: Phase[];
  walked: Seg[];
  stepTime: (seg: Seg, d: number) => number;
  rings: { cx: number; cy: number; t: number }[];
  ghostAt: number;
  end: number;
  rest: Pt | null;
  /** Lead walk: only this part of the segment gets solid footprints. */
  partial?: { seg: Seg; upTo: number };
}

function buildTimeline(g: Geometry, current: number): Timeline {
  const walked = g.segs.filter(s => s.to <= current && s.to > current - WALK_SEGMENTS - 1)
    .slice(-WALK_SEGMENTS);
  const node = g.nodes.get(current);
  const rest: Pt | null = node ? [node.cx, node.top - 8] : null;
  const phases: Phase[] = [];
  const rings: Timeline['rings'] = [];
  const starts = new Map<Seg, number>();
  let t = 0.4;
  let face = 1;
  const faceOf = (s: Seg) => {
    const dx = s.pts[s.pts.length - 1][0] - s.pts[0][0];
    return Math.abs(dx) < 4 ? face : dx > 0 ? -1 : 1; // sprite faces left natively
  };

  if (walked.length === 0) {
    // Current lesson is the first of its stretch (nothing behind it): the
    // ibis comes out of it and shows the way, stopping part-way to the next one.
    const lead = g.segs.find(s => s.from === current);
    if (!lead) {
      if (rest) phases.push({ kind: 'emerge', t0: t, dur: .4, at: rest, face });
      return { phases, walked, stepTime: () => 0, rings, ghostAt: t + .4, end: t + .4, rest };
    }
    const upTo = lead.len * LEAD_STOP;
    face = faceOf(lead);
    phases.push({ kind: 'hidden', t0: 0, dur: t, at: lead.pts[0], face });
    phases.push({ kind: 'emerge', t0: t, dur: .4, at: lead.pts[0], face });
    t += .4;
    const t0 = t;
    phases.push({ kind: 'walk', t0, dur: upTo / SPEED, seg: lead, face, upTo });
    t += upTo / SPEED;
    const p = pointAt(lead.pts, upTo);
    return {
      phases, walked: [lead], rings, end: t, ghostAt: t, rest: [p.x, p.y],
      partial: { seg: lead, upTo },
      stepTime: (_seg, d) => t0 + d / SPEED,
    };
  }

  face = faceOf(walked[0]);
  phases.push({ kind: 'appear', t0: 0, dur: t, at: walked[0].pts[0], face });
  walked.forEach((seg, k) => {
    face = faceOf(seg);
    const dur = seg.len / SPEED;
    starts.set(seg, t);
    phases.push({ kind: 'walk', t0: t, dur, seg, face });
    t += dur;
    const end = seg.pts[seg.pts.length - 1];
    const target = g.nodes.get(seg.to);
    if (target) rings.push({ cx: target.cx, cy: target.cy, t: t + .3 });
    phases.push({ kind: 'dive', t0: t, dur: .4, at: end, face });
    t += .4;
    const next = walked[k + 1];
    const nextAt: Pt = next ? next.pts[0] : (rest ?? end);
    const hid = next ? .4 : .5;
    phases.push({ kind: 'hidden', t0: t, dur: hid, at: nextAt, face });
    t += hid;
    if (next) face = faceOf(next);
    phases.push({ kind: 'emerge', t0: t, dur: .4, at: nextAt, face });
    t += .4;
  });
  return {
    phases, walked, rings, rest, end: t, ghostAt: t - .1,
    stepTime: (seg, d) => (starts.get(seg) ?? 0) + d / SPEED,
  };
}

interface Pose { x: number; y: number; opacity: number; sx: number; sy: number; ty: number; rot: number }

function poseAt(tl: Timeline, t: number): Pose | null {
  const ph = tl.phases;
  if (!ph.length) return null;
  const last = ph[ph.length - 1];
  const p = ph.find(q => t < q.t0 + q.dur) ?? last;
  const k = p.dur > 0 ? Math.min(1, Math.max(0, (t - p.t0) / p.dur)) : 1;
  const base = { sx: p.face, sy: 1, ty: 0, rot: 0, opacity: 1 };
  if (p.kind === 'walk') {
    const pt = pointAt(p.seg.pts, k * (p.upTo ?? p.seg.len));
    if (t >= p.t0 + p.dur) return { ...base, x: pt.x, y: pt.y };
    const w = (t - p.t0) / .45;
    const bob = (1 - Math.cos(w * Math.PI * 2)) / 2;
    return { ...base, x: pt.x, y: pt.y, ty: -3 * bob, rot: 3 * bob };
  }
  const [x, y] = p.at;
  switch (p.kind) {
    case 'appear': return { ...base, x, y, opacity: k };
    case 'dive':   return { ...base, x, y, opacity: 1 - k, sy: 1 - .85 * k, ty: 8 * k };
    case 'hidden': return { ...base, x, y, opacity: 0, sy: .2, ty: 8 };
    case 'emerge': {
      if (t >= p.t0 + p.dur) return { ...base, x, y };
      const e = t - p.t0;
      if (e < .25) {
        const q = e / .25;
        return { ...base, x, y, opacity: Math.min(1, e / .15), sy: .2 + .95 * q, ty: 8 - 12 * q };
      }
      const q = (e - .25) / .15;
      return { ...base, x, y, sy: 1.15 - .15 * q, ty: -4 + 4 * q };
    }
  }
  return null;
}

// ── Drawing ────────────────────────────────────────────────────────────────
const FootPrint: React.FC = () => (
  <path d="M0 4V-3M0 -3L-4.6 -8.5M0 -3V-10M0 -3L4.6 -8.5" />
);

// Ibis line art from the Jogolinga logo (faces left).
const IBIS = [
  'M448 420C455 395 470 375 500 360C530 348 560 348 572 365C580 380 578 410 570 440C560 480 545 520 538 545C532 570 540 585 560 595C590 605 620 605 650 595C690 582 720 562 760 565C850 575 1000 630 1200 785',
  'M448 420C400 462 352 538 330 605C362 532 410 462 448 432Z',
  'M448 432H485C500 435 510 445 515 462C505 505 492 545 490 580C490 610 505 630 530 648C600 700 700 760 765 860',
  'M762 618C720 630 690 642 690 652C690 665 720 690 760 705C850 740 950 760 1165 795L1200 785',
  'M1165 795C1180 800 1190 808 1195 815C1180 822 1150 832 1130 830C1100 815 1060 795 1010 795C960 800 900 810 865 810C840 808 810 795 780 775',
  'M800 808L612 1195',
  'M818 815L850 878L888 1195',
  'M465 1200H672',
  'M745 1200H950',
];

const IbisSprite: React.FC = () => (
  <svg viewBox="285 300 930 930" width={SPRITE} height={SPRITE} aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth={44} strokeLinecap="round" strokeLinejoin="round">
      {IBIS.map((d, i) => <path key={i} d={d} />)}
    </g>
  </svg>
);

const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const IbisTrailWalker: React.FC<Props> = ({ currentIndex, layoutKey, onReady }) => {
  const [geo, setGeo] = useState<Geometry | null>(null);
  const startRef = useRef<number | null>(null);
  const spriteRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const still = useMemo(reducedMotion, []);

  // Measure now and whenever the trail resizes. The trail is our own parent
  // element: a ref passed down from it isn't attached yet when this runs on mount.
  useLayoutEffect(() => {
    const el = rootRef.current?.parentElement as HTMLDivElement | null;
    if (!el) return;
    const run = () => setGeo(measure(el));
    run();
    // Late layout shifts (fonts, images) that don't resize the trail itself.
    const raf = requestAnimationFrame(run);
    const late = window.setTimeout(run, 600);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(run) : null;
    ro?.observe(el);
    window.addEventListener('resize', run);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(late);
      ro?.disconnect();
      window.removeEventListener('resize', run);
    };
  }, [layoutKey]);

  useEffect(() => { onReady?.(!!geo && geo.segs.length > 0); }, [geo, onReady]);

  const tl = useMemo(() => (geo ? buildTimeline(geo, currentIndex) : null), [geo, currentIndex]);

  if (geo && startRef.current === null) startRef.current = performance.now();
  const elapsed = startRef.current === null ? 0 : (performance.now() - startRef.current) / 1000;

  // Drive the ibis sprite directly (no React re-render per frame).
  useEffect(() => {
    if (!tl || still) return;
    let raf = 0;
    const tick = () => {
      const t = (performance.now() - (startRef.current ?? performance.now())) / 1000;
      const pose = poseAt(tl, t);
      const outer = spriteRef.current, inner = innerRef.current;
      if (pose && outer && inner) {
        outer.style.opacity = String(pose.opacity);
        outer.style.transform =
          `translate(${(pose.x - SPRITE * ANCHOR[0]).toFixed(1)}px, ${(pose.y - SPRITE * ANCHOR[1]).toFixed(1)}px)`;
        inner.style.transform =
          `translateY(${pose.ty.toFixed(2)}px) scale(${pose.sx}, ${pose.sy.toFixed(3)}) rotate(${pose.rot.toFixed(2)}deg)`;
      }
      if (t < tl.end + .1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [tl, still]);

  if (!geo || !tl) return <div ref={rootRef} className="ibis-walker" aria-hidden="true" />;

  const walkedSet = new Set(tl.walked);
  const firstWalked = tl.walked[0]?.from ?? currentIndex;
  // Negative delays keep CSS footprints in sync after a re-measure.
  const delay = (t: number) => `${(t - elapsed).toFixed(2)}s`;

  const prints: React.ReactNode[] = [];
  geo.segs.forEach(seg => {
    const segKind = walkedSet.has(seg) ? 'walk' : seg.from < firstWalked ? 'done' : 'ghost';
    const cut = tl.partial?.seg === seg ? tl.partial.upTo : Infinity;
    sample(seg.pts, STEP_GAP).forEach((s, i) => {
      const kind = segKind === 'walk' && s.d > cut ? 'ghost' : segKind;
      const x = s.x + s.nx * 3 * s.side, y = s.y + s.ny * 3 * s.side;
      let cls = 'ibis-print';
      let style: React.CSSProperties | undefined;
      if (still) cls += kind === 'ghost' ? ' ibis-print--ghost-still' : '';
      else if (kind === 'walk') { cls += ' ibis-print--in'; style = { animationDelay: delay(tl.stepTime(seg, s.d) + .15) }; }
      else if (kind === 'ghost') { cls += ' ibis-print--ghost'; style = { animationDelay: delay(tl.ghostAt + i * .04) }; }
      prints.push(
        <g key={`${seg.from}-${i}`} transform={`translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${s.ang.toFixed(1)})`}>
          <g className={cls} style={style}><FootPrint /></g>
        </g>,
      );
    });
  });

  return (
    <div ref={rootRef} className="ibis-walker" aria-hidden="true">
      <svg className="ibis-walker__svg" width={geo.width} height={geo.height}>
        <g className="ibis-walker__prints">{prints}</g>
        {!still && tl.rings.map((r, i) => (
          <circle
            key={`ring-${i}`} className="ibis-ring" cx={r.cx} cy={r.cy} r={36}
            style={{ animationDelay: delay(r.t) }}
          />
        ))}
      </svg>
      {!still && tl.phases.length > 0 && (
        <div ref={spriteRef} className="ibis-walker__sprite" style={{ opacity: 0 }}>
          <div
            ref={innerRef}
            style={{ transformOrigin: `${SPRITE * ANCHOR[0]}px ${SPRITE * ANCHOR[1]}px` }}
          >
            <IbisSprite />
          </div>
        </div>
      )}
      {still && tl.rest && (
        <div
          className="ibis-walker__sprite"
          style={{ transform: `translate(${tl.rest[0] - SPRITE * ANCHOR[0]}px, ${tl.rest[1] - SPRITE * ANCHOR[1]}px)` }}
        >
          <IbisSprite />
        </div>
      )}
    </div>
  );
};

export default IbisTrailWalker;
